/**
 * PostHog Analytics Provider
 * 
 * Minimal event taxonomy - NO SPAM tracking.
 * Only tracks essential product events.
 * 
 * Event Taxonomy:
 * - assessment_started, assessment_completed
 * - opportunity_viewed
 * - feature_locked_viewed, feature_locked_cta_clicked
 * - agent_created, agent_run_started, agent_run_completed
 * - upgrade_clicked
 */

import posthog from 'posthog-js';

// Initialize PostHog only once
let initialized = false;

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

// Routes where session recording is enabled
const SESSION_RECORDING_ROUTES = [
  '/assessment',
  '/assessments',
  '/org-chart',
  '/partner-pro/workspace/assessment',
  '/partner-pro/workspace/org-chart',
];

// Sensitive CSS selectors to mask in recordings
const SENSITIVE_SELECTORS = [
  '[type="password"]',
  '[name*="password"]',
  '[name*="token"]',
  '[name*="api_key"]',
  '[name*="apiKey"]',
  '[name*="secret"]',
  '[data-sensitive]',
  '.api-key-input',
  '.token-input',
  '.file-content',
  '.private-key',
  'textarea[name*="key"]',
  'input[name*="key"]',
];

/**
 * Initialize PostHog SDK
 * Called once at app startup
 */
export const initPostHog = () => {
  if (initialized || !POSTHOG_KEY) {
    if (!POSTHOG_KEY) {
      console.warn('[PostHog] API key not configured - analytics disabled');
    }
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      
      // Person profiles - only for identified users
      person_profiles: 'identified_only',
      
      // Session recording config
      disable_session_recording: true, // Start disabled, enable on specific routes
      session_recording: {
        // Mask all inputs by default
        maskAllInputs: true,
        maskTextSelector: SENSITIVE_SELECTORS.join(', '),
        // Block sensitive elements entirely
        blockSelector: '.posthog-block, [data-posthog-block]',
      },
      
      // Autocapture disabled - we use explicit events only for interactions
      autocapture: false,
      
      // Enable pageview tracking for analytics
      capture_pageview: true,
      capture_pageleave: true,
      
      // Performance
      loaded: (posthog) => {
        // Check if we should enable session recording on current route
        const currentPath = window.location.pathname;
        const shouldRecord = SESSION_RECORDING_ROUTES.some(route => 
          currentPath.startsWith(route)
        );
        
        if (shouldRecord) {
          posthog.startSessionRecording();
          console.debug('[PostHog] Session recording enabled for:', currentPath);
        }
        
        console.info('[PostHog] Pageview tracking enabled');
      },
    });

    initialized = true;
    console.info('[PostHog] Initialized successfully');
  } catch (error) {
    console.error('[PostHog] Initialization failed:', error);
  }
};

/**
 * Identify user after login
 * Sets distinct_id and person properties
 */
export const identifyUser = (user, organization, planTier) => {
  if (!initialized || !user?.id) return;

  try {
    posthog.identify(user.id, {
      email: user.email,
      user_role: user.role || 'user',
      plan_tier: planTier || 'basic',
      org_id: organization?.organization_id || null,
      org_name: organization?.name || null,
    });
    
    console.debug('[PostHog] User identified:', user.id);
  } catch (error) {
    console.error('[PostHog] Identify failed:', error);
  }
};

/**
 * Track pageview for SPA navigation
 * Call this on route changes in React Router
 */
export const trackPageview = (path, properties = {}) => {
  if (!initialized) return;
  
  try {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      $pathname: path || window.location.pathname,
      $host: window.location.host,
      $referrer: document.referrer,
      ...properties
    });
    console.debug('[PostHog] Pageview tracked:', path || window.location.pathname);
  } catch (error) {
    console.error('[PostHog] Pageview tracking failed:', error);
  }
};

/**
 * Reset user on logout
 */
export const resetUser = () => {
  if (!initialized) return;
  
  try {
    posthog.reset();
    console.debug('[PostHog] User reset');
  } catch (error) {
    console.error('[PostHog] Reset failed:', error);
  }
};

/**
 * Update person properties (e.g., after plan change)
 */
export const updatePersonProperties = (properties) => {
  if (!initialized) return;
  
  try {
    posthog.setPersonProperties(properties);
  } catch (error) {
    console.error('[PostHog] Update properties failed:', error);
  }
};

/**
 * Check and toggle session recording based on route
 */
export const checkSessionRecording = (pathname) => {
  if (!initialized) return;
  
  const shouldRecord = SESSION_RECORDING_ROUTES.some(route => 
    pathname.startsWith(route)
  );
  
  try {
    if (shouldRecord && !posthog.sessionRecordingStarted()) {
      posthog.startSessionRecording();
      console.debug('[PostHog] Session recording started for:', pathname);
    } else if (!shouldRecord && posthog.sessionRecordingStarted()) {
      posthog.stopSessionRecording();
      console.debug('[PostHog] Session recording stopped');
    }
  } catch (error) {
    // Session recording methods may not exist in older versions
    console.debug('[PostHog] Session recording toggle skipped');
  }
};

// ============================================================================
// EVENT TRACKING FUNCTIONS - Strict Taxonomy
// ============================================================================

/**
 * Assessment Events
 */
export const trackAssessmentStarted = (properties = {}) => {
  if (!initialized) return;
  posthog.capture('assessment_started', {
    plan_tier: properties.plan_tier,
    org_id: properties.org_id,
  });
};

export const trackAssessmentCompleted = (properties) => {
  if (!initialized) return;
  posthog.capture('assessment_completed', {
    duration_ms: properties.duration_ms,
    valid_opportunity_count: properties.valid_opportunity_count,
    coming_soon_count: properties.coming_soon_count,
    has_org_chart: properties.has_org_chart,
    stop_reason: properties.stop_reason, // 'completed', 'error', 'cancelled'
    plan_tier: properties.plan_tier,
    org_id: properties.org_id,
  });
};

/**
 * Opportunity Events
 */
export const trackOpportunityViewed = (properties = {}) => {
  if (!initialized) return;
  posthog.capture('opportunity_viewed', {
    opportunity_id: properties.opportunity_id,
    opportunity_title: properties.opportunity_title,
    plan_tier: properties.plan_tier,
  });
};

/**
 * Feature Gating Events
 */
export const trackFeatureLockedViewed = (properties = {}) => {
  if (!initialized) return;
  posthog.capture('feature_locked_viewed', {
    feature_name: properties.feature_name,
    plan_tier: properties.plan_tier,
    page_path: properties.page_path || window.location.pathname,
  });
};

export const trackFeatureLockedCtaClicked = (properties = {}) => {
  if (!initialized) return;
  posthog.capture('feature_locked_cta_clicked', {
    feature_name: properties.feature_name,
    plan_tier: properties.plan_tier,
    page_path: properties.page_path || window.location.pathname,
  });
};

/**
 * Agent Events
 */
export const trackAgentCreated = (properties = {}) => {
  if (!initialized) return;
  posthog.capture('agent_created', {
    agent_id: properties.agent_id,
    agent_name: properties.agent_name,
    source: properties.source, // 'opportunity', 'marketplace', 'manual'
    plan_tier: properties.plan_tier,
    org_id: properties.org_id,
  });
};

export const trackAgentRunStarted = (properties = {}) => {
  if (!initialized) return;
  posthog.capture('agent_run_started', {
    agent_id: properties.agent_id,
    session_id: properties.session_id,
    input_type: properties.input_type, // 'text', 'file', etc.
    plan_tier: properties.plan_tier,
  });
};

export const trackAgentRunCompleted = (properties = {}) => {
  if (!initialized) return;
  posthog.capture('agent_run_completed', {
    agent_id: properties.agent_id,
    session_id: properties.session_id,
    status: properties.status, // 'success', 'error'
    duration_ms: properties.duration_ms,
    plan_tier: properties.plan_tier,
  });
};

/**
 * Billing Events
 */
export const trackUpgradeClicked = (properties = {}) => {
  if (!initialized) return;
  posthog.capture('upgrade_clicked', {
    source: properties.source, // 'feature_gate', 'pricing_page', 'banner'
    current_plan: properties.current_plan,
    target_plan: properties.target_plan,
    feature_name: properties.feature_name,
  });
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  init: initPostHog,
  identify: identifyUser,
  reset: resetUser,
  updateProperties: updatePersonProperties,
  checkRecording: checkSessionRecording,
  
  // Events
  assessmentStarted: trackAssessmentStarted,
  assessmentCompleted: trackAssessmentCompleted,
  opportunityViewed: trackOpportunityViewed,
  featureLockedViewed: trackFeatureLockedViewed,
  featureLockedCtaClicked: trackFeatureLockedCtaClicked,
  agentCreated: trackAgentCreated,
  agentRunStarted: trackAgentRunStarted,
  agentRunCompleted: trackAgentRunCompleted,
  upgradeClicked: trackUpgradeClicked,
  
  // Pageview tracking
  trackPageview: trackPageview,
};
